import { Badge } from "@/components/ui/badge";
import { Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Car, CalendarClock, ExternalLink, Plus, CornerDownRight, CheckCircle2, Calendar, AlertTriangle, TrendingUp, TrendingDown, Users, Clock, DollarSign, XCircle, ArrowRightLeft, BarChart3, Wrench, Trophy, Wallet, ListChecks, CalendarOff, UserMinus } from "lucide-react";
import { VehicleStatusBadge } from "@/components/fleet/VehicleStatusBadge";
import type { VehicleStatus } from "@/types/vehicle";
import type { SearchResult } from "../AiAssistantSheet";
import ReactMarkdown from "react-markdown";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import {
  findAssistantPresetByQuery,
  getAssistantPreset,
  type AssistantPresetId,
} from "@/lib/assistantQueryPresets";

const STATUS_COLORS: Record<string, string> = {
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rented: "bg-blue-50 text-blue-700 border-blue-200",
  cleaning: "bg-amber-50 text-amber-700 border-amber-200",
  maintenance: "bg-orange-50 text-orange-700 border-orange-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
};

const TABLE_CELL_COLORS: Record<string, string> = {
  "Available": "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400",
  "Rented": "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400",
  "Partial": "bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400",
  "Maintenance": "bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400",
  "Blocked": "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400",
  "Cleaning": "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
};

const SEVERITY_STYLES: Record<string, string> = {
  critical: "border-destructive/30 bg-destructive/5 text-destructive",
  warning: "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/20 dark:text-amber-400",
  info: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-950/20 dark:text-blue-400",
};

export interface AiAction {
  type: "book" | "follow_up";
  query: string;
  presetId?: AssistantPresetId;
}

// ── Contextual Follow-up Chips ────────────────────────────────────

function FollowUpChips({ result, onAction }: { result: SearchResult; onAction?: (action: AiAction) => void }) {
  if (!onAction) return null;

  const chips: { label: string; query: string; presetId?: AssistantPresetId }[] = [];
  const addPresetChip = (presetId: AssistantPresetId, queryOverride?: string) => {
    const preset = getAssistantPreset(presetId);
    chips.push({
      label: preset.label,
      query: queryOverride ?? preset.query,
      presetId,
    });
  };

  switch (result.result_type) {
    case "availability":
      addPresetChip(
        "availabilityTable",
        `${getAssistantPreset("availabilityTable").query}${result.parsed_filters ? ` from ${result.parsed_filters.start_at} to ${result.parsed_filters.end_at}` : ""}`,
      );
      break;
    case "daily_briefing":
      addPresetChip("attentionNeeded");
      addPresetChip("revenueReport");
      break;
    case "customer_360":
      break;
    case "attention_needed":
      addPresetChip("dailyBriefing");
      addPresetChip("utilizationReport");
      break;
    case "revenue_report":
      addPresetChip("utilizationReport");
      addPresetChip("customerInsights");
      break;
    case "utilization_report":
      addPresetChip("availabilityToday");
      addPresetChip("revenueReport");
      break;
    case "booking_list":
      addPresetChip("availabilityToday");
      addPresetChip("dailyBriefing");
      break;
    case "maintenance_list":
      addPresetChip("expenseReport");
      addPresetChip("dailyBriefing");
      break;
    case "expense_report":
      addPresetChip("maintenanceReport");
      addPresetChip("revenueReport");
      break;
    case "profit_report":
      addPresetChip("revenueForecast");
      addPresetChip("cashFlow");
      break;
    case "revenue_forecast":
      addPresetChip("profitByVehicle");
      addPresetChip("bookingGaps");
      break;
    case "cash_flow":
      addPresetChip("profitByVehicle");
      addPresetChip("revenueReport");
      break;
    case "action_queue":
      addPresetChip("dailyBriefing");
      addPresetChip("bookingGaps");
      break;
    case "gap_analysis":
      addPresetChip("actionList");
      addPresetChip("followUpList");
      break;
    case "churn_risk":
      addPresetChip("bookingGaps");
      addPresetChip("actionList");
      break;
    case "upcoming_schedule":
      addPresetChip("actionList");
      addPresetChip("bookingGaps");
      break;
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 pt-2">
      {chips.map((chip) => (
        <button key={`${chip.label}-${chip.query}`} onClick={() => onAction({ type: "follow_up", query: chip.query, presetId: chip.presetId })}
          className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
          {chip.label}
        </button>
      ))}
    </div>
  );
}

function ClarificationOptions({ result, onAction }: { result: SearchResult; onAction?: (action: AiAction) => void }) {
  if (result.result_type !== "clarification" || !result.clarification?.options?.length) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pick the closest action</p>
      </div>
      <div className="space-y-2">
        {result.clarification.options.map((option) => {
          const preset = findAssistantPresetByQuery(option.query);
          return (
          <Button
            key={`${option.intent}-${option.query}`}
            type="button"
            variant="outline"
            className="h-auto w-full justify-between px-3 py-3 text-left"
            disabled={!onAction}
            onClick={() => onAction?.({ type: "follow_up", query: option.query, presetId: preset?.id })}
          >
            <span className="text-sm font-medium">{option.label}</span>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
          );
        })}
      </div>
    </div>
  );
}

// ── Availability Snippet Table ────────────────────────────────────

function AvailabilitySnippet({ snippet }: { snippet: { headers: string[]; rows: string[][] } }) {
  return (
    <div className="mt-2 rounded-lg border border-border overflow-hidden">
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide px-3 py-1.5 bg-muted/50">Availability Grid</p>
      <div className="overflow-x-auto max-h-[40vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              {snippet.headers.map((header, i) => (
                <TableHead key={i} className="text-[10px] font-semibold whitespace-nowrap px-2 py-1.5 first:sticky first:left-0 first:z-10 first:bg-muted/90">
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {snippet.rows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <TableCell
                    key={cellIdx}
                    className={`text-[10px] whitespace-nowrap px-2 py-1 first:sticky first:left-0 first:z-10 first:bg-card first:font-medium ${cellIdx > 0 ? (TABLE_CELL_COLORS[cell] || "") : ""}`}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── CTA Buttons ───────────────────────────────────────────────────

function VehicleActionButton({ vehicle, resultType, bookingId, parsedFilters, onAction }: {
  vehicle?: SearchResult["vehicles"][number];
  resultType: string;
  bookingId?: string;
  parsedFilters?: SearchResult["parsed_filters"];
  onAction?: (action: AiAction) => void;
}) {
  const navigate = useNavigate();

  if (resultType === "availability" && vehicle) {
    const params = new URLSearchParams({ new: "1", vehicle_id: vehicle.id });
    if (parsedFilters?.start_at) params.set("start", parsedFilters.start_at);
    if (parsedFilters?.end_at) params.set("end", parsedFilters.end_at);
    return (
      <Button variant="outline" size="sm" className="text-[11px] h-7 gap-1" onClick={() => navigate(`/bookings?${params.toString()}`)}>
        <Plus className="h-3 w-3" /> Book
      </Button>
    );
  }

  if ((resultType === "vehicle_detail" || resultType === "vehicle_list") && vehicle?.id) {
    return (
      <Button variant="ghost" size="sm" className="text-[11px] h-7 gap-1" onClick={() => navigate(`/fleet/${vehicle.id}`)}>
        <ExternalLink className="h-3 w-3" /> View
      </Button>
    );
  }

  if (resultType === "returning_soon" && bookingId) {
    return (
      <Button variant="outline" size="sm" className="text-[11px] h-7 gap-1" onClick={() => navigate(`/bookings/${bookingId}/return`)}>
        <CornerDownRight className="h-3 w-3" /> Return
      </Button>
    );
  }

  return null;
}

// ── Issue Action Button ───────────────────────────────────────────

function IssueActionButton({ issue }: { issue: NonNullable<SearchResult["issues"]>[number] }) {
  const navigate = useNavigate();

  if (issue.type === "overdue_return" && issue.booking_id) {
    return (
      <Button variant="outline" size="sm" className="text-[10px] h-6 gap-0.5 mt-1" onClick={() => navigate(`/bookings/${issue.booking_id}/return`)}>
        <CornerDownRight className="h-3 w-3" /> Start Return
      </Button>
    );
  }
  if (issue.type === "insurance_expiry" && issue.vehicle_id) {
    return (
      <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-0.5 mt-1" onClick={() => navigate(`/fleet/${issue.vehicle_id}`)}>
        <ExternalLink className="h-3 w-3" /> View Vehicle
      </Button>
    );
  }
  if (issue.type === "escalated_task" || issue.type === "pending_review") {
    return (
      <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-0.5 mt-1" onClick={() => navigate("/maintenance")}>
        <ExternalLink className="h-3 w-3" /> View Task
      </Button>
    );
  }
  return null;
}

// ── Maintenance List Card ─────────────────────────────────────────

const SEVERITY_DOT: Record<string, string> = {
  critical: "bg-destructive", high: "bg-orange-500",
  medium: "bg-warning", low: "bg-muted-foreground", planned: "bg-info",
};

const MAINT_STATUS_BADGE: Record<string, string> = {
  open: "bg-muted text-muted-foreground",
  in_progress: "bg-warning/10 text-warning",
  review: "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
  closed: "bg-success/10 text-success",
  rejected: "bg-destructive/10 text-destructive",
};

function MaintenanceListCard({ result }: { result: SearchResult }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <Wrench className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-semibold">Maintenance Jobs</p>
        {result.total_cost != null && result.total_cost > 0 && (
          <span className="ml-auto text-sm font-bold text-destructive">-${result.total_cost.toLocaleString()}</span>
        )}
      </div>
      {result.by_status && Object.keys(result.by_status).length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(result.by_status).map(([status, count]) => (
            <span key={status} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${MAINT_STATUS_BADGE[status] || "bg-muted text-muted-foreground"}`}>
              {count} {status.replace("_", " ")}
            </span>
          ))}
        </div>
      )}
      <div className="space-y-0">
        {(result.jobs || []).map((job, i) => (
          <div key={job.id ?? i} className="flex items-center justify-between py-2 border-t border-border/50 first:border-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`h-2 w-2 rounded-full shrink-0 ${SEVERITY_DOT[job.severity] || "bg-muted-foreground"}`} />
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{job.title || job.type}</p>
                <p className="text-[10px] text-muted-foreground truncate">{job.vehicle}{job.plate ? ` · ${job.plate}` : ""}{job.vendor ? ` · ${job.vendor}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 ml-2">
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${MAINT_STATUS_BADGE[job.status] || ""}`}>
                {(job.status || "open").replace("_", " ")}
              </span>
              {job.cost != null && job.cost > 0 && (
                <span className="text-xs font-semibold text-destructive">-${job.cost.toLocaleString()}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Expense Report Card ───────────────────────────────────────────

function ExpenseReportCard({ result }: { result: SearchResult }) {
  const profit = result.net_profit ?? 0;
  const revenue = typeof result.revenue === "number" ? result.revenue : 0;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4 mt-2">
      <div className="flex items-center gap-2">
        {profit >= 0
          ? <TrendingUp className="h-4 w-4 text-success" />
          : <TrendingDown className="h-4 w-4 text-destructive" />
        }
        <p className="text-sm font-semibold">Profit & Loss</p>
        {result.period && (
          <span className="ml-auto text-[10px] text-muted-foreground">{result.period.from} – {result.period.to}</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-success/10 p-2">
          <p className="text-base font-bold text-success">${revenue.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">Revenue</p>
        </div>
        <div className="rounded-lg bg-destructive/10 p-2">
          <p className="text-base font-bold text-destructive">${(result.expenses || 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">Expenses</p>
        </div>
        <div className={`rounded-lg p-2 ${profit >= 0 ? "bg-success/10" : "bg-destructive/10"}`}>
          <p className={`text-base font-bold ${profit >= 0 ? "text-success" : "text-destructive"}`}>
            {profit >= 0 ? "+" : ""}${Math.abs(profit).toLocaleString()}
          </p>
          <p className="text-[10px] text-muted-foreground">Net</p>
        </div>
      </div>
      {result.expense_breakdown && result.expense_breakdown.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Expense Breakdown</p>
          {result.expense_breakdown.map((item, i) => (
            <div key={i} className="flex items-center justify-between text-xs py-0.5 border-t border-border/30 first:border-0">
              <span className="text-foreground truncate">{item.type}</span>
              <div className="flex items-center gap-1.5 ml-2 shrink-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${MAINT_STATUS_BADGE[item.status] || "bg-muted text-muted-foreground"}`}>
                  {item.status}
                </span>
                <span className="text-destructive font-medium">-${item.cost.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Profit by Vehicle Card ────────────────────────────────────────

function ProfitReportCard({ result }: { result: SearchResult }) {
  const items = result.profit_items || [];
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-500" />
        <p className="text-sm font-semibold">Profit by Vehicle</p>
      </div>
      <div className="space-y-0">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-t border-border/50 first:border-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[11px] font-bold text-muted-foreground w-5 shrink-0">{i + 1}</span>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{item.vehicle}</p>
                <p className="text-[10px] text-muted-foreground">{item.plate}</p>
              </div>
            </div>
            <div className="text-right shrink-0 ml-2 space-y-0.5">
              <p className={`text-xs font-bold ${item.net_profit >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                {item.net_profit >= 0 ? "+" : ""}${item.net_profit.toLocaleString()}
              </p>
              <p className="text-[10px] text-muted-foreground">{item.margin}% margin</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Revenue Forecast Card ─────────────────────────────────────────

function RevenueForecastCard({ result }: { result: SearchResult }) {
  const weeks = result.forecast_weeks || [];
  const total = weeks.reduce((s, w) => s + w.revenue, 0);
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">30-Day Forecast</p>
        <span className="ml-auto text-sm font-bold text-emerald-600">${total.toLocaleString()}</span>
      </div>
      <div className="space-y-2">
        {weeks.map((w, i) => (
          <div key={i} className="flex items-center justify-between text-xs py-1 border-t border-border/50 first:border-0">
            <span className="text-muted-foreground truncate">{w.week}</span>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-muted-foreground">{w.confirmed} booking{w.confirmed !== 1 ? "s" : ""}</span>
              <span className="font-semibold text-foreground">${w.revenue.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Cash Flow Card ────────────────────────────────────────────────

function CashFlowCard({ result }: { result: SearchResult }) {
  const cf = result.cash_flow;
  if (!cf) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Cash Position</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 p-2.5 text-center">
          <p className="text-base font-bold text-emerald-700 dark:text-emerald-400">${cf.deposits_held.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">Deposits Held</p>
        </div>
        <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 p-2.5 text-center">
          <p className="text-base font-bold text-blue-700 dark:text-blue-400">${cf.outstanding_owed.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">Outstanding</p>
        </div>
        <div className="rounded-lg bg-destructive/10 p-2.5 text-center">
          <p className="text-base font-bold text-destructive">${cf.scheduled_costs.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">Scheduled Costs</p>
        </div>
        <div className={`rounded-lg p-2.5 text-center ${cf.net_position >= 0 ? "bg-emerald-50 dark:bg-emerald-950/20" : "bg-destructive/10"}`}>
          <p className={`text-base font-bold ${cf.net_position >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`}>
            {cf.net_position >= 0 ? "+" : ""}${cf.net_position.toLocaleString()}
          </p>
          <p className="text-[10px] text-muted-foreground">Net Position</p>
        </div>
      </div>
    </div>
  );
}

// ── Action Queue Card ─────────────────────────────────────────────

const URGENCY_STYLES: Record<string, string> = {
  high: "border-destructive/30 bg-destructive/5 text-destructive",
  medium: "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/20 dark:text-amber-400",
  low: "border-border bg-card text-muted-foreground",
};

function ActionQueueCard({ result }: { result: SearchResult }) {
  const navigate = useNavigate();
  const items = result.action_items || [];
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-2 mt-2">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Action Queue</p>
      </div>
      {items.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20 p-3 text-center">
          <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
          <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">All clear! No immediate actions needed.</p>
        </div>
      ) : items.map((item, i) => (
        <div key={i} className={`rounded-lg border p-3 space-y-1 ${URGENCY_STYLES[item.urgency] || URGENCY_STYLES.low}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0">
              <span className="text-[11px] font-bold shrink-0 mt-0.5">{item.priority}.</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold">{item.title}</p>
                {item.detail && <p className="text-[11px] opacity-80">{item.detail}</p>}
              </div>
            </div>
            {item.type === "overdue_return" && item.booking_id && (
              <Button variant="outline" size="sm" className="text-[10px] h-6 shrink-0" onClick={() => navigate(`/bookings/${item.booking_id}/return`)}>
                <CornerDownRight className="h-3 w-3" />
              </Button>
            )}
            {item.type === "maintenance" && (
              <Button variant="ghost" size="sm" className="text-[10px] h-6 shrink-0" onClick={() => navigate("/maintenance")}>
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
            {item.type === "insurance" && item.vehicle_id && (
              <Button variant="ghost" size="sm" className="text-[10px] h-6 shrink-0" onClick={() => navigate(`/fleet/${item.vehicle_id}`)}>
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Gap Analysis Card ─────────────────────────────────────────────

function GapAnalysisCard({ result }: { result: SearchResult }) {
  const navigate = useNavigate();
  const items = result.gap_items || [];
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <CalendarOff className="h-4 w-4 text-amber-500" />
        <p className="text-sm font-semibold">Calendar Gaps</p>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No significant idle windows found — your fleet is well-booked!</p>
      ) : (
        <div className="space-y-0">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-t border-border/50 first:border-0">
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{item.vehicle} · {item.plate}</p>
                <p className="text-[10px] text-muted-foreground">{item.idle_from} → {item.idle_to}{item.last_customer ? ` · Last: ${item.last_customer}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                <span className="text-xs font-bold text-amber-600">{item.days}d idle</span>
                <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => navigate(`/bookings?new=1&start=${item.idle_from}`)}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Churn Risk Card ───────────────────────────────────────────────

function ChurnRiskCard({ result }: { result: SearchResult }) {
  const navigate = useNavigate();
  const items = result.churn_items || [];
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 mt-2">
      <div className="flex items-center gap-2">
        <UserMinus className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-semibold">Follow-Up List</p>
      </div>
      {items.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20 p-3 text-center">
          <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
          <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">All customers have recent activity!</p>
        </div>
      ) : (
        <div className="space-y-0">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-t border-border/50 first:border-0">
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{item.name}</p>
                <p className="text-[10px] text-muted-foreground">Last booking: {item.last_booking} · {item.total_bookings} total</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                <span className={`text-[10px] font-semibold ${item.days_inactive > 90 ? "text-destructive" : "text-amber-600"}`}>{item.days_inactive}d ago</span>
                <Button variant="ghost" size="sm" className="text-[10px] h-6" onClick={() => navigate("/customers")}>
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Upcoming Schedule Card ────────────────────────────────────────

const EVENT_STYLES: Record<string, { border: string; icon: string; label: string }> = {
  pickup:          { border: "border-l-blue-500",    icon: "🚗", label: "Pickup"   },
  return:          { border: "border-l-emerald-500", icon: "🔄", label: "Return"   },
  maintenance:     { border: "border-l-orange-500",  icon: "🔧", label: "Maint."   },
  insurance_expiry:{ border: "border-l-red-500",     icon: "🛡️", label: "Insurance"},
};

function UpcomingScheduleCard({ result }: { result: SearchResult }) {
  const navigate = useNavigate();
  const days = result.schedule_days || [];

  if (days.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 mt-2 text-center space-y-2">
        <CalendarOff className="h-8 w-8 mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Nothing coming up — fleet is quiet!</p>
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-xl border border-border bg-card mt-2 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b border-border">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-semibold">Upcoming Schedule</p>
        <span className="ml-auto text-[10px] text-muted-foreground">Next {result.lookahead_days ?? 7} days · {result.total_events ?? 0} events</span>
      </div>
      <div className="divide-y divide-border/50">
        {days.map((day) => (
          <div key={day.date}>
            <div className={`px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${day.date === today ? "bg-primary/10 text-primary" : "bg-muted/30 text-muted-foreground"}`}>
              {day.label}
            </div>
            <div className="divide-y divide-border/30">
              {day.events.map((ev, i) => {
                const style = EVENT_STYLES[ev.type] || EVENT_STYLES.pickup;
                return (
                  <div key={i} className={`flex items-center gap-3 pl-3 pr-3 py-2.5 border-l-2 ${style.border} ${ev.urgency === "high" ? "bg-destructive/5" : ""}`}>
                    <span className="text-base shrink-0">{style.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{ev.title}</p>
                      {ev.subtitle && <p className="text-[10px] text-muted-foreground truncate">{ev.subtitle}</p>}
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ev.urgency === "high" ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"}`}>
                        {style.label}
                      </span>
                      {ev.booking_id && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigate(`/bookings/${ev.booking_id}`)}>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                      {ev.maintenance_id && !ev.booking_id && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigate("/maintenance")}>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                      {ev.vehicle_id && !ev.booking_id && !ev.maintenance_id && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigate(`/fleet/${ev.vehicle_id}`)}>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────

type RevenueReport = Exclude<SearchResult["revenue"], number | undefined>;

function getRevenueReport(revenue: SearchResult["revenue"]): RevenueReport | null {
  if (!revenue || typeof revenue !== "object") return null;
  return revenue;
}

export function AiResultCards({ result, onAction, onClose }: { result: SearchResult; onAction?: (action: AiAction) => void; onClose?: () => void }) {
  const navigate = useNavigate();
  const revenueReport = getRevenueReport(result.revenue);
  const timeWindowLabel = result.time_window?.label || (result.time_window ? `${result.time_window.from_at} → ${result.time_window.to_at}` : null);
  return (
    <div className="space-y-3 mt-2">
      <div className="text-sm font-medium text-foreground prose prose-sm max-w-none">
        <ReactMarkdown>{result.summary}</ReactMarkdown>
      </div>

      {/* Filters badge row */}
      {result.parsed_filters && (
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="text-xs">{result.parsed_filters.start_at} → {result.parsed_filters.end_at}</Badge>
          {result.parsed_filters.category && <Badge variant="secondary" className="text-xs capitalize">{result.parsed_filters.category}</Badge>}
          {result.parsed_filters.max_daily_rate && <Badge variant="secondary" className="text-xs">≤ ${result.parsed_filters.max_daily_rate}/day</Badge>}
          {result.parsed_filters.min_daily_rate && <Badge variant="secondary" className="text-xs">≥ ${result.parsed_filters.min_daily_rate}/day</Badge>}
        </div>
      )}

      {timeWindowLabel && (
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className="text-xs">
            {result.time_window?.granularity === "datetime" ? "Date & time" : "Date range"}: {timeWindowLabel}
          </Badge>
        </div>
      )}

      {result.status_filter && <Badge variant="secondary" className="text-xs capitalize">{result.status_filter}</Badge>}

      {result.result_type === "clarification" && (
        <ClarificationOptions result={result} onAction={onAction} />
      )}

      {/* Availability snippet table (shown before vehicle cards) */}
      {result.result_type === "availability" && result.availability_snippet && (
        <AvailabilitySnippet snippet={result.availability_snippet} />
      )}

      {/* Status summary cards */}
      {result.result_type === "status_summary" && result.status_counts && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {Object.entries(result.status_counts).map(([status, count]) => (
            <div key={status} className={`rounded-lg border p-3 text-center ${STATUS_COLORS[status] || "bg-muted text-foreground border-border"}`}>
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-xs capitalize font-medium">{status}</p>
            </div>
          ))}
          {result.total != null && (
            <div className="rounded-lg border border-border bg-card p-3 text-center col-span-2">
              <p className="text-2xl font-bold text-foreground">{result.total}</p>
              <p className="text-xs font-medium text-muted-foreground">Total Fleet</p>
            </div>
          )}
        </div>
      )}

      {/* Returning soon cards */}
      {result.result_type === "returning_soon" && result.vehicles.length > 0 && (
        <div className="space-y-2 mt-2">
          {result.vehicles.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
              {v.image_url ? (
                <img src={v.image_url} alt={`${v.make} ${v.model}`} className="h-12 w-16 rounded-md object-cover" />
              ) : (
                <div className="flex h-12 w-16 items-center justify-center rounded-md bg-muted">
                  <Car className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{v.make} {v.model}</p>
                <p className="text-xs text-muted-foreground">{v.plate_number}</p>
                {v.customer_name && <p className="text-xs text-muted-foreground">Rented to {v.customer_name}</p>}
              </div>
              <div className="text-right shrink-0 space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-primary">
                  <CalendarClock className="h-3.5 w-3.5" />
                  {v.booking_end ? new Date(v.booking_end).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A"}
                </div>
                <VehicleActionButton vehicle={v} resultType={result.result_type} bookingId={v.booking_id} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Next available card */}
      {result.result_type === "next_available" && result.vehicles.length > 0 && (
        <div className="space-y-2 mt-2">
          {result.vehicles.map((v) => (
            <div key={v.id} className="rounded-lg border border-border bg-card p-4 space-y-2">
              <div className="flex items-center gap-3">
                {v.image_url ? (
                  <img src={v.image_url} alt={`${v.make} ${v.model}`} className="h-14 w-20 rounded-md object-cover" />
                ) : (
                  <div className="flex h-14 w-20 items-center justify-center rounded-md bg-muted">
                    <Car className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{v.make} {v.model} {v.year ? `(${v.year})` : ""}</p>
                  <p className="text-xs text-muted-foreground">{v.plate_number}</p>
                  <VehicleStatusBadge status={v.status as VehicleStatus} />
                </div>
                <VehicleActionButton vehicle={v} resultType="vehicle_detail" />
              </div>
              {v.available_from && v.status !== "available" && (
                <div className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">
                    Available from {new Date(v.available_from).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              )}
              {v.current_customer && (
                <p className="text-xs text-muted-foreground">Currently with {v.current_customer}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Booking stats */}
      {result.result_type === "booking_stats" && result.stats && result.stats.length > 0 && (
        <div className="space-y-2 mt-2">
          {result.stats.map((s, i) => (
            <div key={s.vehicle_id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm shrink-0">
                {i + 1}
              </div>
              {s.image_url ? (
                <img src={s.image_url} alt={`${s.make} ${s.model}`} className="h-10 w-14 rounded-md object-cover" />
              ) : (
                <div className="flex h-10 w-14 items-center justify-center rounded-md bg-muted">
                  <Car className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{s.make} {s.model}</p>
                <p className="text-xs text-muted-foreground">{s.plate_number}</p>
              </div>
              <div className="text-right shrink-0 space-y-0.5">
                <p className="text-sm font-semibold text-foreground">
                  {result.metric === "revenue" ? `$${s.total_revenue.toLocaleString()}` : `${s.rental_count} rentals`}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {result.metric === "revenue" ? `${s.rental_count} rentals` : `$${s.total_revenue.toLocaleString()} rev`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Standard vehicle cards (availability, vehicle_list, vehicle_detail) */}
      {result.vehicles.length > 0 && !["status_summary", "returning_soon", "next_available", "booking_stats", "booking_created", "daily_briefing", "customer_360", "revenue_report", "attention_needed", "booking_modified", "booking_cancelled", "utilization_report", "customer_insights", "booking_list", "profit_report", "revenue_forecast", "cash_flow", "action_queue", "gap_analysis", "churn_risk", "upcoming_schedule", "clarification"].includes(result.result_type) && (
        <div className="space-y-2 mt-2">
          {result.vehicles.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
              {v.image_url ? (
                <img src={v.image_url} alt={`${v.make} ${v.model}`} className="h-12 w-16 rounded-md object-cover" />
              ) : (
                <div className="flex h-12 w-16 items-center justify-center rounded-md bg-muted">
                  <Car className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {v.make} {v.model} {v.year ? `(${v.year})` : ""}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <span>{v.plate_number}</span>
                  {v.category && <Badge variant="outline" className="text-[10px] capitalize px-1.5 py-0">{v.category}</Badge>}
                </div>
                {v.current_customer_name && (
                  <p className="text-xs text-muted-foreground mt-0.5">Rented to {v.current_customer_name}</p>
                )}
              </div>
              <div className="text-right shrink-0 space-y-1">
                {result.result_type === "availability" && v.daily_rate != null && (
                  <p className="text-sm font-semibold text-foreground">
                    ${v.daily_rate}<span className="text-xs font-normal text-muted-foreground">/day</span>
                  </p>
                )}
                {v.status && (result.result_type === "vehicle_list" || result.result_type === "vehicle_detail") && (
                  <VehicleStatusBadge status={v.status as VehicleStatus} />
                )}
                <VehicleActionButton vehicle={v} resultType={result.result_type} parsedFilters={result.parsed_filters} onAction={onAction} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule entries */}
      {result.result_type === "schedule" && result.schedule && result.schedule.length > 0 && (
        <div className="space-y-2 mt-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Upcoming Bookings</p>
          {result.schedule.map((entry) => (
            <div key={entry.booking_id} className="rounded-lg border border-border bg-card p-3 space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{entry.customer_name || "No customer"}</p>
                <Badge variant="secondary" className="text-[10px] capitalize">{entry.status.replace(/_/g, " ")}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {new Date(entry.start_date).toLocaleDateString()} → {new Date(entry.end_date).toLocaleDateString()}
              </p>
              {entry.daily_rate != null && <p className="text-xs text-muted-foreground">${entry.daily_rate}/day</p>}
            </div>
          ))}
        </div>
      )}

      {result.result_type === "schedule" && (!result.schedule || result.schedule.length === 0) && result.vehicles.length > 0 && (
        <p className="text-sm text-muted-foreground mt-2">No upcoming bookings — vehicle is free!</p>
      )}

      {result.vehicles.length === 0 && !["status_summary", "schedule", "text", "booking_stats", "table", "daily_briefing", "customer_360", "revenue_report", "attention_needed", "booking_modified", "booking_cancelled", "utilization_report", "customer_insights", "booking_list", "clarification"].includes(result.result_type) && !(result.result_type === "booking_stats" && result.stats?.length) && (
        <p className="text-sm text-muted-foreground mt-2">Try broadening your search.</p>
      )}

      {result.result_type === "booking_stats" && (!result.stats || result.stats.length === 0) && (
        <p className="text-sm text-muted-foreground mt-2">No booking data found for that period.</p>
      )}

      {/* Booking list card */}
      {result.result_type === "booking_list" && result.bookings && result.bookings.length > 0 && (
        <div className="space-y-2 mt-2">
          {result.bookings.map((b) => (
            <div key={b.id} className="rounded-lg border border-border bg-card p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{b.customer_name}</p>
                <Badge variant="secondary" className="text-[10px] capitalize">{(b.status || "").replace(/_/g, " ")}</Badge>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Car className="h-3 w-3" />
                <span>{b.vehicle_name}</span>
                <span className="text-muted-foreground/50">•</span>
                <span>{b.vehicle_plate}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {b.start_date} → {b.end_date}
                </span>
                {b.total_price > 0 && <span className="font-medium text-foreground">${b.total_price}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {result.result_type === "booking_list" && (!result.bookings || result.bookings.length === 0) && (
        <p className="text-sm text-muted-foreground mt-2">No bookings found matching your criteria.</p>
      )}


      {result.result_type === "booking_created" && result.booking && (
        <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Booking Confirmed</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-muted-foreground">Vehicle</p>
              <p className="font-medium text-foreground">{result.booking.vehicle_name}</p>
              <p className="text-muted-foreground">{result.booking.vehicle_plate}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Customer</p>
              <p className="font-medium text-foreground">{result.booking.customer_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Dates</p>
              <p className="font-medium text-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {result.booking.start_date} → {result.booking.end_date}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Total</p>
              <p className="font-medium text-foreground">${result.booking.total_price} ({result.booking.days}d × ${result.booking.daily_rate})</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full text-xs gap-1" onClick={() => navigate("/bookings")}>
            <ExternalLink className="h-3 w-3" /> View Bookings
          </Button>
        </div>
      )}

      {result.result_type === "booking_created" && !result.booking && (
        <p className="text-sm text-destructive mt-2">Booking could not be created. Check the details and try again.</p>
      )}

      {/* ── Daily Briefing Card ──────────────────────────────────── */}
      {result.result_type === "daily_briefing" && result.briefing && (
        <div className="mt-2 space-y-3">
          {/* Fleet status mini grid */}
          {result.briefing.fleet_status && (
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(result.briefing.fleet_status as Record<string, number>).map(([status, count]) => (
                <div key={status} className={`rounded-md border p-2 text-center ${STATUS_COLORS[status] || "bg-muted border-border"}`}>
                  <p className="text-lg font-bold">{count as number}</p>
                  <p className="text-[10px] capitalize font-medium">{status}</p>
                </div>
              ))}
            </div>
          )}
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <Car className="h-4 w-4 mx-auto text-primary mb-1" />
              <p className="text-lg font-bold text-foreground">{result.briefing.pickups_today}</p>
              <p className="text-[10px] text-muted-foreground">Pickups</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <CornerDownRight className="h-4 w-4 mx-auto text-primary mb-1" />
              <p className="text-lg font-bold text-foreground">{result.briefing.returns_today}</p>
              <p className="text-[10px] text-muted-foreground">Returns</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <AlertTriangle className="h-4 w-4 mx-auto text-destructive mb-1" />
              <p className="text-lg font-bold text-foreground">{result.briefing.overdue_count}</p>
              <p className="text-[10px] text-muted-foreground">Overdue</p>
            </div>
          </div>
          {result.briefing.revenue_recent > 0 && (
            <div className="rounded-lg border border-border bg-card p-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              <span className="text-sm text-muted-foreground">Yesterday's revenue:</span>
              <span className="text-sm font-semibold text-foreground">${result.briefing.revenue_recent.toLocaleString()}</span>
            </div>
          )}
          {/* Sections */}
          {result.briefing.sections?.map((section, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-3 space-y-1.5">
              <p className="text-xs font-semibold text-foreground">{section.icon} {section.title}</p>
              {section.items.map((item: string, j: number) => (
                <p key={j} className="text-xs text-muted-foreground pl-5">• {item}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Customer 360 Card ────────────────────────────────────── */}
      {result.result_type === "customer_360" && result.customer_profile && (
        <div className="mt-2 rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{result.customer_profile.full_name}</p>
              <p className="text-xs text-muted-foreground">{result.customer_profile.phone || result.customer_profile.email || "No contact"}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-md bg-muted p-2">
              <p className="text-muted-foreground">Total Bookings</p>
              <p className="text-lg font-bold text-foreground">{result.customer_profile.total_bookings}</p>
            </div>
            <div className="rounded-md bg-muted p-2">
              <p className="text-muted-foreground">Total Spend</p>
              <p className="text-lg font-bold text-foreground">${result.customer_profile.total_spend?.toLocaleString()}</p>
            </div>
            <div className="rounded-md bg-muted p-2">
              <p className="text-muted-foreground">Paid</p>
              <p className="font-semibold text-emerald-600">${result.customer_profile.total_paid?.toLocaleString()}</p>
            </div>
            <div className="rounded-md bg-muted p-2">
              <p className="text-muted-foreground">Outstanding</p>
              <p className={`font-semibold ${(result.customer_profile.outstanding || 0) > 0 ? "text-destructive" : "text-foreground"}`}>
                ${(result.customer_profile.outstanding || 0).toLocaleString()}
              </p>
            </div>
          </div>
          {result.customer_profile.active_rental && (
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-1">
              <p className="text-xs font-semibold text-primary flex items-center gap-1"><Car className="h-3 w-3" /> Active Rental</p>
              <p className="text-xs text-foreground">{result.customer_profile.active_rental.vehicle_name} ({result.customer_profile.active_rental.vehicle_plate})</p>
              <p className="text-xs text-muted-foreground">{result.customer_profile.active_rental.start_date} → {result.customer_profile.active_rental.end_date}</p>
            </div>
          )}
          {result.customer_profile.recent_bookings?.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Recent Bookings</p>
              {result.customer_profile.recent_bookings.map((b, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-border last:border-0">
                  <span className="text-foreground">{b.vehicle}</span>
                  <Badge variant="secondary" className="text-[10px] capitalize">{b.status?.replace(/_/g, " ")}</Badge>
                  <span className="text-muted-foreground">${b.total}</span>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" className="w-full text-xs gap-1"
            onClick={() => navigate(result.customer_profile.id ? `/customers?highlight=${result.customer_profile.id}` : "/customers")}>
            <ExternalLink className="h-3 w-3" /> View Customer
          </Button>
        </div>
      )}

      {result.result_type === "customer_360" && !result.customer_profile && (
        <p className="text-sm text-muted-foreground mt-2">Customer not found. Try a different name, phone, or email.</p>
      )}

      {/* ── Revenue Report Card ──────────────────────────────────── */}
      {result.result_type === "revenue_report" && revenueReport && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <DollarSign className="h-4 w-4 mx-auto text-emerald-600 mb-1" />
              <p className="text-lg font-bold text-foreground">${revenueReport.total_revenue?.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">Total Revenue</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-600 mb-1" />
              <p className="text-lg font-bold text-foreground">${revenueReport.total_collected?.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">Collected</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <Clock className="h-4 w-4 mx-auto text-amber-600 mb-1" />
              <p className="text-lg font-bold text-foreground">${revenueReport.total_pending?.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">Pending</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <TrendingUp className="h-4 w-4 mx-auto text-primary mb-1" />
              <p className="text-lg font-bold text-foreground">{revenueReport.collection_rate}%</p>
              <p className="text-[10px] text-muted-foreground">Collection Rate</p>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Bookings: <span className="font-semibold text-foreground">{revenueReport.total_bookings}</span></span>
            <span className="text-muted-foreground">Avg Rate: <span className="font-semibold text-foreground">${revenueReport.avg_daily_rate}/day</span></span>
            <span className="text-muted-foreground">Deposits: <span className="font-semibold text-foreground">${revenueReport.deposits_held?.toLocaleString()}</span></span>
          </div>
          <Button variant="outline" size="sm" className="w-full text-xs gap-1" onClick={() => navigate("/financials")}>
            <ExternalLink className="h-3 w-3" /> View Financials
          </Button>
        </div>
      )}

      {/* ── Attention Needed Card ────────────────────────────────── */}
      {result.result_type === "attention_needed" && result.issues && (
        <div className="mt-2 space-y-2">
          {result.issues.length === 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20 p-4 text-center">
              <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-600 mb-1" />
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">All clear!</p>
            </div>
          )}
          {result.issues.map((issue, i) => (
            <div key={i} className={`rounded-lg border p-3 space-y-0.5 ${SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.info}`}>
              <div className="flex items-center gap-2">
                {issue.severity === "critical" && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                {issue.severity === "warning" && <Clock className="h-3.5 w-3.5 shrink-0" />}
                {issue.severity === "info" && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
                <p className="text-xs font-semibold">{issue.title}</p>
              </div>
              {issue.detail && <p className="text-[11px] opacity-80 pl-5">{issue.detail}</p>}
              <div className="pl-5">
                <IssueActionButton issue={issue} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Booking Modified Card ────────────────────────────────── */}
      {result.result_type === "booking_modified" && result.modification && (
        <div className="mt-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">Booking Modified</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-muted-foreground">Customer</p>
              <p className="font-medium text-foreground">{result.modification.customer_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Vehicle</p>
              <p className="font-medium text-foreground">{result.modification.vehicle_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Return Date</p>
              <p className="font-medium text-foreground"><s className="text-muted-foreground">{result.modification.old_end_date}</s> → {result.modification.new_end_date}</p>
            </div>
            <div>
              <p className="text-muted-foreground">New Total</p>
              <p className="font-medium text-foreground">${result.modification.new_total} ({result.modification.days}d)</p>
            </div>
          </div>
        </div>
      )}

      {result.result_type === "booking_modified" && !result.modification && (
        <p className="text-sm text-destructive mt-2">Could not modify booking. Check the details and try again.</p>
      )}

      {/* ── Booking Cancelled Card ───────────────────────────────── */}
      {result.result_type === "booking_cancelled" && result.cancellation && (
        <div className="mt-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm font-semibold text-destructive">Booking Cancelled</p>
          </div>
          <div className="text-xs space-y-1">
            <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium text-foreground">{result.cancellation.customer_name}</span></p>
            <p><span className="text-muted-foreground">Vehicle:</span> <span className="font-medium text-foreground">{result.cancellation.vehicle_name}</span></p>
            <p><span className="text-muted-foreground">Period:</span> <span className="font-medium text-foreground">{result.cancellation.start_date} → {result.cancellation.end_date}</span></p>
            {result.cancellation.reason && <p><span className="text-muted-foreground">Reason:</span> <span className="text-foreground">{result.cancellation.reason}</span></p>}
          </div>
        </div>
      )}

      {result.result_type === "booking_cancelled" && !result.cancellation && (
        <p className="text-sm text-destructive mt-2">Could not cancel booking. Check the details and try again.</p>
      )}

      {/* ── Utilization Report Card ──────────────────────────────── */}
      {result.result_type === "utilization_report" && result.utilization && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <BarChart3 className="h-4 w-4 mx-auto text-primary mb-1" />
              <p className="text-lg font-bold text-foreground">{result.utilization.avg_utilization}%</p>
              <p className="text-[10px] text-muted-foreground">Avg Utilization</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <Car className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
              <p className="text-lg font-bold text-foreground">{result.utilization.vehicles?.length || 0}</p>
              <p className="text-[10px] text-muted-foreground">Vehicles</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <AlertTriangle className="h-4 w-4 mx-auto text-amber-600 mb-1" />
              <p className="text-lg font-bold text-foreground">{result.utilization.idle_count}</p>
              <p className="text-[10px] text-muted-foreground">Idle</p>
            </div>
          </div>
          {result.utilization.vehicles?.length > 0 && (
            <div className="space-y-1.5">
              {result.utilization.vehicles.map((v, i) => (
                <div key={i} className="flex items-center gap-2 text-xs rounded-md border border-border bg-card px-3 py-2">
                  <span className="font-medium text-foreground flex-1 truncate">{v.vehicle}</span>
                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${v.utilization_pct >= 70 ? "bg-emerald-500" : v.utilization_pct >= 30 ? "bg-amber-500" : "bg-destructive"}`} style={{ width: `${v.utilization_pct}%` }} />
                  </div>
                  <span className="text-muted-foreground w-10 text-right">{v.utilization_pct}%</span>
                  <span className="text-muted-foreground w-16 text-right">${v.revenue?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Customer Insights Card ───────────────────────────────── */}
      {result.result_type === "customer_insights" && result.insights && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <Users className="h-4 w-4 mx-auto text-primary mb-1" />
              <p className="text-lg font-bold text-foreground">{result.insights.total_customers}</p>
              <p className="text-[10px] text-muted-foreground">Total</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <TrendingUp className="h-4 w-4 mx-auto text-emerald-600 mb-1" />
              <p className="text-lg font-bold text-foreground">{result.insights.repeat_customers}</p>
              <p className="text-[10px] text-muted-foreground">Repeat</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <Plus className="h-4 w-4 mx-auto text-blue-600 mb-1" />
              <p className="text-lg font-bold text-foreground">{result.insights.new_customers}</p>
              <p className="text-[10px] text-muted-foreground">New</p>
            </div>
          </div>
          {result.insights.top_customers?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Top Customers</p>
              {result.insights.top_customers.map((c, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-[10px] shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{c.name}</p>
                    <p className="text-[10px] text-muted-foreground">{c.bookings} booking{c.bookings > 1 ? "s" : ""}</p>
                  </div>
                  <p className="text-xs font-semibold text-foreground">${c.revenue?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" className="w-full text-xs gap-1" onClick={() => navigate("/customers")}>
            <ExternalLink className="h-3 w-3" /> View Customers
          </Button>
        </div>
      )}

      {/* Dynamic table rendering */}
      {result.result_type === "table" && result.table && (
        <div className="mt-2 rounded-lg border border-border overflow-hidden">
          {result.table_meta?.type === "availability_matrix" && (
            <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Availability Matrix</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-[10px] gap-1 text-muted-foreground hover:text-primary"
                onClick={() => {
                  const params = new URLSearchParams({ start: result.table_meta!.start_date, end: result.table_meta!.end_date });
                  if (result.table_meta!.category) params.set("category", result.table_meta!.category);
                  if (result.table_meta!.min_daily_rate) params.set("minRate", String(result.table_meta!.min_daily_rate));
                  if (result.table_meta!.max_daily_rate) params.set("maxRate", String(result.table_meta!.max_daily_rate));
                  onClose?.();
                  navigate(`/availability-table?${params.toString()}`);
                }}
              >
                <Maximize2 className="h-3 w-3" /> Expand
              </Button>
            </div>
          )}
          <div className="overflow-x-auto max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  {result.table.headers.map((header, i) => (
                    <TableHead key={i} className="text-[11px] font-semibold whitespace-nowrap px-2 py-2 first:sticky first:left-0 first:z-10 first:bg-muted/90">
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.table.rows.map((row, rowIdx) => (
                  <TableRow key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <TableCell
                        key={cellIdx}
                        className={`text-[11px] whitespace-nowrap px-2 py-1.5 first:sticky first:left-0 first:z-10 first:bg-card first:font-medium ${cellIdx > 0 ? (TABLE_CELL_COLORS[cell] || "") : ""}`}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {result.table.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={result.table.headers.length} className="text-center text-sm text-muted-foreground py-4">
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Maintenance list card */}
      {result.result_type === "maintenance_list" && (
        <MaintenanceListCard result={result} />
      )}

      {/* Expense / P&L report card */}
      {result.result_type === "expense_report" && (
        <ExpenseReportCard result={result} />
      )}

      {/* Profit by vehicle card */}
      {result.result_type === "profit_report" && (
        <ProfitReportCard result={result} />
      )}

      {/* Revenue forecast card */}
      {result.result_type === "revenue_forecast" && (
        <RevenueForecastCard result={result} />
      )}

      {/* Cash flow / position card */}
      {result.result_type === "cash_flow" && (
        <CashFlowCard result={result} />
      )}

      {/* Action priority queue card */}
      {result.result_type === "action_queue" && (
        <ActionQueueCard result={result} />
      )}

      {/* Booking gap analysis card */}
      {result.result_type === "gap_analysis" && (
        <GapAnalysisCard result={result} />
      )}

      {/* Customer churn risk card */}
      {result.result_type === "churn_risk" && (
        <ChurnRiskCard result={result} />
      )}

      {/* Upcoming schedule card */}
      {result.result_type === "upcoming_schedule" && (
        <UpcomingScheduleCard result={result} />
      )}

      {/* Contextual follow-up chips */}
      <FollowUpChips result={result} onAction={onAction} />
    </div>
  );
}
